import React from "react";
import { GoogleMap, MarkerF } from "@react-google-maps/api";

import { Notice, NoticeText } from "components/ui/Notice";

import { usePracticeLocationMap } from "../../hooks/usePracticeLocationMap";
import styles from "./PracticeLocationMap.module.css";

const MAP_ZOOM = 15;
const COORDINATE_DECIMALS = 6;

/* GoogleMap sizes itself from a style object prop, not from a class, so this one
   object is the map's own API rather than styling of our markup. */
const MAP_CONTAINER_STYLE = { height: "400px", width: "100%" };

const MapPlaceholder = () => (
  <div className={styles.mapPlaceholder}>
    <p className={styles.mapPlaceholderText}>Loading map...</p>
  </div>
);

const CoordinateReadout = ({ coordinates, canDragMarker }) => (
  <div className={styles.readout}>
    {!!canDragMarker && (
      <p className={styles.readoutLine}>
        Drag the marker to precisely set your practice location
      </p>
    )}

    {!!coordinates && (
      <p className={styles.readoutLine} data-testid="practice-map-coordinates">
        Current coordinates: {coordinates.lat.toFixed(COORDINATE_DECIMALS)},{" "}
        {coordinates.lng.toFixed(COORDINATE_DECIMALS)}
      </p>
    )}

    <p className={styles.readoutLine}>
      Enter an address or eircode above to automatically locate and pin the
      address
    </p>
  </div>
);

/**
 * The practice's location: the pin on the map, and the coordinates the form saves.
 *
 * The map is a convenience — latitude, longitude and geoHash live in the same Formik
 * form as the address, so everything here can be saved with the map absent, which is
 * what the notice says when Google refuses to load.
 */
export const PracticeLocationMap = ({
  mapsScript,
  eircode,
  fullAddress,
  initialLatitude,
  initialLongitude,
  initialGeoHash,
  onEircodeChange,
  onLocationChange,
}) => {
  const {
    coordinates,
    isLocating,
    addressMessage,
    isMapUnavailable,
    handleMarkerDragEnd,
  } = usePracticeLocationMap({
    mapsScript,
    eircode,
    fullAddress,
    initialLatitude,
    initialLongitude,
    initialGeoHash,
    onEircodeChange,
    onLocationChange,
  });

  return (
    <div className={styles.location}>
      {!!isLocating && (
        <Notice tone="info" testId="practice-map-loading">
          <NoticeText>Searching for location...</NoticeText>
        </Notice>
      )}

      {!!addressMessage && (
        <Notice tone="info" testId="practice-map-address-notice">
          <NoticeText>{addressMessage}</NoticeText>
        </Notice>
      )}

      {isMapUnavailable ? (
        <Notice tone="info" testId="practice-map-unavailable">
          <NoticeText>
            The map could not be loaded, so the practice location cannot be
            pinned here. The address fields above are what gets saved, so the
            address and its coordinates can still be entered and saved without
            the map.
          </NoticeText>
        </Notice>
      ) : (
        <div className={styles.mapFrame} data-testid="practice-map">
          {mapsScript.isReady && !!coordinates ? (
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              zoom={MAP_ZOOM}
              center={coordinates}
            >
              <MarkerF
                position={coordinates}
                onDragEnd={handleMarkerDragEnd}
                draggable
                title="Practice Location (Drag to adjust)"
              />
            </GoogleMap>
          ) : (
            <MapPlaceholder />
          )}
        </div>
      )}

      <CoordinateReadout
        coordinates={coordinates}
        canDragMarker={!isMapUnavailable}
      />
    </div>
  );
};
